import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Lot : famille "sous-ensemble de stock tracé".
 *
 * Sous-ensemble de stock d'un `product`, identifié par un numéro de lot ou une
 * date limite, pour la traçabilité, les rappels produits et la gestion des
 * péremptions. Construite sur le modèle "famille + type" de la bible (patron :
 * `asset`).
 *
 * Le socle ne connaît pas la nature métier concrète ("lot de vaccin", "date
 * limite de produit frais", "millésime") : il connaît la famille générique
 * `lot`. Le vocabulaire métier est porté par le champ `type` (chaîne libre
 * déclarée par l'app).
 *
 * Références vers d'autres familles = de simples ids (aucun import de schéma) :
 * `productId` (l'article dont relève le lot) est le CŒUR de la famille, donc
 * requis ; `supplierContactId` (référence `contact` du fournisseur) et
 * `receivedMovementId` (référence `stock-movement` de réception) sont
 * optionnels. Les liens stock, care-record et incident ne vivent PAS ici :
 * c'est l'autre entité qui porte un `lotId`.
 *
 * Convention de patron (cf. asset) :
 *   - `xCoreFields`  : les champs MÉTIER seuls (sans les champs standard).
 *   - `XSchema`      : famille complète = champs standard + champs métier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit à la création, VALIDÉ
 *     AVANT toute écriture Firestore.
 */

/** Champs métier de la famille lot (hors champs standard du socle). */
export const lotCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "peremption", "serie", "millesime"). */
  type: z.string().min(1),
  /** Article dont relève le lot (référence `product`, cœur de la famille). */
  productId: z.string().min(1),
  /** Numéro de lot du fabricant / de la mise en lot, optionnel (un lot peut n'être identifié que par sa date limite). */
  lotNumber: z.string().min(1).optional(),
  /** Date limite de consommation / de péremption, optionnel. */
  expiresAt: z.date().optional(),
  /** Quantité rattachée au lot, optionnel. */
  quantity: z.number().int().optional(),
  /** Fournisseur du lot (référence `contact`), optionnel. */
  supplierContactId: z.string().min(1).optional(),
  /** Mouvement de stock de réception à l'origine du lot (référence `stock-movement`), optionnel. */
  receivedMovementId: z.string().min(1).optional(),
} as const;

/** Schéma complet de la famille lot (champs standard + champs métier). */
export const LotSchema = z.object({
  ...baseEntityFields,
  ...lotCoreFields,
});

/** Type d'un lot. */
export type Lot = z.infer<typeof LotSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const LotCreateInputSchema = z.object({
  ...lotCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'un lot. */
export type LotCreateInput = z.infer<typeof LotCreateInputSchema>;

/**
 * Schéma du patch de mise à jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont gérés par le SDK : aucun des deux n'est accepté ici.
 */
export const LotUpdateInputSchema = z
  .object({ ...lotCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise à jour d'un lot. */
export type LotUpdateInput = z.infer<typeof LotUpdateInputSchema>;
